import type { CardData, CharacterDto, ProjectDto, ShotData, ShotDto } from '@studio/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ImagePlus,
  Images,
  LayoutGrid,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  Square,
  Upload,
  UserRound,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useProjectCall } from '@/components/project/common';
import { StoryboardSheet } from '@/components/project/StoryboardSheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog, ProgressBar } from '@/components/ui/controls';
import { Input } from '@/components/ui/form';
import { errorMessage, uploadImage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { isActive, useCharacters } from '@/lib/queries';

/**
 * The storyboard: one still per shot, made on the GPU in seconds, so wrong speakers, wrong places and wrong faces
 * are caught on a picture before a video is made. An approved still becomes the first frame of the shot's video.
 */
export function FramesTab({ project }: { project: ProjectDto }) {
  const call = useProjectCall(project.id);
  const qc = useQueryClient();
  const { me } = useAuth();
  const { data: characters = [] } = useCharacters();
  const canDraw = me?.imagesAvailable !== false;
  const [confirmAll, setConfirmAll] = useState(false);
  const [view, setView] = useState<'panels' | 'sheet'>('panels');
  // Everyone who appears in a shot: without a cast picture they look different in every still.
  const shown = new Set(
    project.shots.flatMap((s) => (s.kind === 'shot' ? (s.data as ShotData).cast.filter((m) => m.type === 'character').map((m) => m.id) : [])),
  );
  const people = characters.filter((c) => shown.has(c.id) || project.castIds.includes(c.id));
  const noPicture = people.filter((c) => !c.images.length && !(c.castJob && isActive(c.castJob.status)));
  const drawingCast = people.filter((c) => c.castJob && isActive(c.castJob.status));
  const makeCast = async () => {
    const r = await call<{ queued: number; skipped: string[]; project: ProjectDto }>(
      `/api/projects/${project.id}/cast-pictures`,
      { method: 'POST' },
      (x) => x.project,
    );
    if (r) {
      void qc.invalidateQueries({ queryKey: ['characters'] });
      if (r.skipped.length) toast.warning(`No description, so no picture: ${r.skipped.join(', ')} — write how they look first`);
    }
  };
  const shots = project.shots.filter((s) => s.kind === 'shot');
  const withFrame = shots.filter((s) => s.frame).length;
  const approved = shots.filter((s) => (s.data as ShotData).frame?.approved).length;
  const working = shots.filter((s) => s.frameJob && isActive(s.frameJob.status)).length;
  const missing = shots.length - withFrame;
  const makeFrames = (missingOnly: boolean) =>
    call(
      `/api/projects/${project.id}/frames`,
      { method: 'POST', body: { missingOnly } },
      (r: { project: ProjectDto }) => r.project,
    );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto min-w-48">
          <div className="text-sm font-medium">
            {withFrame} of {shots.length} stills · {approved} approved
            {working > 0 && <span className="text-muted-foreground"> · {working} being drawn</span>}
          </div>
          <ProgressBar value={shots.length ? approved / shots.length : 0} tone="success" className="mt-1 max-w-xs" />
        </div>
        <Button onClick={() => void makeFrames(true)} disabled={!missing || !canDraw}>
          <Sparkles /> {!missing ? 'Every shot has a still' : missing === shots.length ? 'Make all stills' : `Make ${missing} missing`}
        </Button>
        {withFrame > 0 && canDraw && (
          <Button variant="outline" onClick={() => setConfirmAll(true)}>
            <RefreshCw /> Redraw all
          </Button>
        )}
        {withFrame > approved && (
          <Button
            variant="outline"
            onClick={() => call(`/api/projects/${project.id}/frames/approve`, { method: 'POST' })}
          >
            <Check /> Approve all
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Check every still before making videos: is the right person talking (mouth open) and everyone else silent? Is
        the place right? An <b>approved</b> still becomes the first frame of that shot's video, so the video continues
        exactly from the storyboard. Characters with a cast picture look the same in every still.
      </p>
      {people.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border p-2">
          <span className="text-xs font-medium">Cast:</span>
          {people.map((c) => (
            <span
              key={c.id}
              className={`flex items-center gap-1 rounded-full border py-0.5 pr-2 pl-0.5 text-xs ${c.images.length ? '' : 'border-warning/60 bg-warning/10'}`}
              title={c.images.length ? 'Has a picture: looks the same in every still' : 'No picture: may look different in every still'}
            >
              {c.images[0] ? (
                <img src={c.images[0].url} alt="" className="size-6 rounded-full object-cover object-top" />
              ) : c.castJob && isActive(c.castJob.status) ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <span className="grid size-6 place-items-center rounded-full bg-muted">?</span>
              )}
              {c.name}
            </span>
          ))}
          {noPicture.length > 0 && canDraw && (
            <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={() => void makeCast()}>
              <UserRound /> Make {noPicture.length} character sheet{noPicture.length > 1 ? 's' : ''} first
            </Button>
          )}
          {drawingCast.length > 0 && (
            <span className="ml-auto text-xs text-muted-foreground">Drawing {drawingCast.length} cast picture(s)…</span>
          )}
        </div>
      )}
      <div className="flex gap-1">
        <Button size="sm" variant={view === 'panels' ? 'default' : 'outline'} onClick={() => setView('panels')}>
          <Images /> Panels
        </Button>
        <Button size="sm" variant={view === 'sheet' ? 'default' : 'outline'} onClick={() => setView('sheet')}>
          <LayoutGrid /> Storyboard sheet
        </Button>
      </div>
      {!canDraw && (
        <p className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">
          FreeVideo's picture model is not installed on the GPU yet, so stills cannot be drawn. You can still upload
          your own still for any shot and approve it.
        </p>
      )}
      {!project.shots.length && (
        <Card className="p-8 text-center text-sm text-muted-foreground">No shots yet — draft the storyboard first.</Card>
      )}
      {view === 'sheet' ? (
        <StoryboardSheet project={project} characters={characters} />
      ) : (
        <div
          className={
            project.aspect === '16:9'
              ? 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3'
              : 'grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4'
          }
        >
          {project.shots.map((s, i) =>
            s.kind === 'card' ? (
              <CardTile key={s.id} shot={s} index={i} aspect={project.aspect} />
            ) : (
              <FrameTile key={s.id} project={project} shot={s} index={i} characters={characters} />
            ),
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title="Redraw every still?"
        description="Every shot gets a new still that needs approving again. Shots being drawn now are skipped."
        confirmLabel="Redraw all"
        onConfirm={() => {
          setConfirmAll(false);
          void makeFrames(false);
        }}
      />
    </div>
  );
}

const aspectClass = (aspect: string) =>
  aspect === '16:9' ? 'aspect-video' : aspect === '1:1' ? 'aspect-square' : 'aspect-[9/16]';

function CardTile({ shot, index, aspect }: { shot: ShotDto; index: number; aspect: string }) {
  const d = shot.data as CardData;
  return (
    <Card className="overflow-hidden">
      <div className={`${aspectClass(aspect)} flex items-center justify-center bg-muted p-3 text-center`}>
        <div className="text-sm font-bold whitespace-pre-line" style={{ color: d.textColor }}>
          {d.text}
        </div>
      </div>
      <div className="p-2 text-xs text-muted-foreground">
        {index + 1}. Text card · {d.seconds} s
      </div>
    </Card>
  );
}

function FrameTile({
  project,
  shot,
  index,
  characters,
}: {
  project: ProjectDto;
  shot: ShotDto;
  index: number;
  characters: CharacterDto[];
}) {
  const call = useProjectCall(project.id);
  const { me } = useAuth();
  const canDraw = me?.imagesAvailable !== false;
  const d = shot.data as ShotData;
  const job = shot.frameJob;
  const busy = !!job && isActive(job.status);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const byId = new Map(characters.map((c) => [c.id, c]));
  for (const c of project.cast) byId.set(c.id, c);
  const names = new Map([...byId.values()].map((c) => [c.id, c.callName || c.name]));
  const onScreen = d.cast.filter((m) => m.type === 'character');
  const unpictured = onScreen.filter((m) => !byId.get(m.id)?.images.length).map((m) => names.get(m.id) ?? 'someone');
  const speakers = new Set(d.lines.filter((l) => l.text.trim()).map((l) => l.characterId));
  const firstLine = d.lines.find((l) => l.text.trim());
  const make = (body: { edit?: string } = {}) =>
    call(`/api/projects/shots/${shot.id}/frame`, { method: 'POST', body });
  const upload = async (file: File) => {
    setUploading(true);
    try {
      const m = await uploadImage(file);
      await call(`/api/projects/shots/${shot.id}/frame`, { method: 'PUT', body: { mediaId: m.id } });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <Card className={`overflow-hidden ${d.frame?.approved ? 'ring-2 ring-success' : ''}`}>
      <div className={`relative ${aspectClass(project.aspect)} bg-muted`}>
        {shot.frame ? (
          <a href={shot.frame.url} target="_blank" rel="noreferrer">
            <img src={shot.frame.url} alt={`Shot ${index + 1} still`} className="size-full object-cover" />
          </a>
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-1 p-3 text-center text-xs text-muted-foreground">
            <Images className="size-6" />
            No still yet
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 p-2 text-center text-xs">
            <Loader2 className="size-5 animate-spin" />
            {job.progressNote ?? 'Drawing…'}
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 flex gap-1">
          <Badge className="bg-background/90 text-foreground">{index + 1}</Badge>
          {d.frame?.approved && (
            <Badge variant="success" className="bg-background/90">
              <Check className="size-3" /> approved
            </Badge>
          )}
        </div>
      </div>
      <div className="space-y-2 p-2">
        <div className="text-xs">
          <span className="font-medium">{d.label || `Shot ${index + 1}`}</span>
          <span className="text-muted-foreground"> · {d.seconds} s</span>
        </div>
        {onScreen.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {onScreen.map((m) => (
              <Badge
                key={m.id}
                variant={speakers.has(m.id) ? 'default' : 'secondary'}
                title={speakers.has(m.id) ? 'Speaks in this shot' : 'Silent in this shot'}
              >
                {speakers.has(m.id) ? '🗣 ' : ''}
                {names.get(m.id) ?? 'character'}
              </Badge>
            ))}
          </div>
        )}
        {firstLine && (
          <p className="hindi line-clamp-2 text-xs text-muted-foreground">
            <b>{firstLine.characterId ? names.get(firstLine.characterId) : firstLine.speakerName || 'Voice'}:</b>{' '}
            {firstLine.text}
          </p>
        )}
        {job?.status === 'failed' && <p className="text-xs text-destructive">{job.error}</p>}
        {unpictured.length > 0 && (
          <p className="text-[11px] text-warning">No cast picture: {unpictured.join(', ')} may look different here.</p>
        )}
        {editing && (
          <form
            className="flex gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (!edit.trim()) return;
              void make({ edit }).then((r) => {
                if (r) {
                  setEditing(false);
                  setEdit('');
                }
              });
            }}
          >
            <Input
              autoFocus
              value={edit}
              onChange={(e) => setEdit(e.target.value)}
              placeholder="e.g. the waiter's mouth closed"
              className="h-8 text-xs"
            />
            <Button type="submit" size="sm" className="h-8 px-2" aria-label="Change the still">
              <Check />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditing(false)} aria-label="Close">
              <X />
            </Button>
          </form>
        )}
        <div className="flex flex-wrap gap-1">
          {busy ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => call(`/api/projects/images/${job.id}/cancel`, { method: 'POST' })}
            >
              <Square /> Cancel
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant={shot.frame ? 'outline' : 'default'}
                className="h-7 px-2 text-xs"
                disabled={!canDraw}
                onClick={() => void make()}
              >
                {shot.frame ? <RefreshCw /> : <Sparkles />} {shot.frame ? 'Redraw' : 'Make'}
              </Button>
              {shot.frame && (
                <Button
                  size="sm"
                  variant={d.frame?.approved ? 'outline' : 'default'}
                  className="h-7 px-2 text-xs"
                  onClick={() =>
                    call(`/api/projects/shots/${shot.id}/frame/approve`, {
                      method: 'POST',
                      body: { approved: !d.frame?.approved },
                    })
                  }
                >
                  <Check /> {d.frame?.approved ? 'Unapprove' : 'Approve'}
                </Button>
              )}
              {shot.frame && !editing && canDraw && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(true)}>
                  <Pencil /> Change
                </Button>
              )}
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            loading={uploading}
            onClick={() => fileRef.current?.click()}
            title="Use your own picture as this shot's still"
          >
            {shot.frame ? <Upload /> : <ImagePlus />} Upload
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
        </div>
        {d.startFrom && d.frame?.approved && (
          <p className="text-[11px] text-muted-foreground">
            This shot continues from another shot's last frame, so its video does not start from this still.
          </p>
        )}
        {shot.framePrompt && (
          <details className="text-[11px] text-muted-foreground">
            <summary className="cursor-pointer">What the still is drawn from</summary>
            <p className="mt-1 whitespace-pre-wrap">{shot.framePrompt}</p>
          </details>
        )}
      </div>
    </Card>
  );
}
